import React, { FC, useCallback, useMemo, ChangeEvent, useContext, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import DownloadIcon from '@mui/icons-material/Download'

import Window from '../dialog/Window'

import {
  EditorState,
  Modifier,
} from 'draft-js'

import {
  ITextSelection,
  getSelection,
  applySelection,
  getSelectedText,
} from './utils'

import EditorContext from './EditorContext'

import {
  ITextEditor_EditorUpdate,
} from '@kaidam/fc-shared/src/types/texteditor'

import {
  IData_Marketing_TextEditor_Prompt,
} from '@kaidam/fc-shared/src/types/schema/marketing'

const useStyles = makeStyles(theme => ({
  select: {
    fontSize: '0.8em',
    color: theme.palette.grey[800],
  },
}))

interface ToolbarMarketingPromptProps {
  prompts: IData_Marketing_TextEditor_Prompt[],
  onRunPrompt: {
    (text: string, prompt: IData_Marketing_TextEditor_Prompt): Promise<string>,
  },
  editorState: EditorState,
  setEditorState: ITextEditor_EditorUpdate,
}

const ToolbarMarketingPrompt: FC<React.PropsWithChildren<ToolbarMarketingPromptProps>> = ({
  prompts,
  onRunPrompt,
  editorState,
  setEditorState,
}) => {

  const [winOpen, setWinOpen] = useState(false)
  const [selectionState, setSelectionState] = useState<ITextSelection>()
  const [selectionText, setSelectionText] = useState<string[]>([])
  const [newText, setNewText] = useState<string[]>([])
  const editorContext = useContext(EditorContext)

  const disabled = useMemo<boolean>(() => {
    return editorState.getSelection().isCollapsed()
  }, [
    editorState,
  ])

  const closePromptWindow = () => {
    setWinOpen(false)
    setNewText([])
    setTimeout(() => {
      editorContext.focus()
    }, 1)
    setTimeout(() => {
      setEditorState(applySelection(editorState, selectionState))
    }, 100)
  }

  const runPrompt = async (prompt: IData_Marketing_TextEditor_Prompt) => {
    setNewText([])
    const result = await onRunPrompt(selectionText.join('\n'), prompt)
    setNewText(result.split(/\n+/))
  }

  const onAcceptNewText = () => {
    setTimeout(() => {
      editorContext.focus()
    }, 1)
    setTimeout(() => {
      const newEditorState = applySelection(editorState, selectionState)
      const contentState = newEditorState.getCurrentContent()
      const newSelectionState = newEditorState.getSelection()

      const newContentState = Modifier.replaceText(
        contentState,
        newSelectionState,
        newText.join("\n"),
      )

      setEditorState(EditorState.push(
        newEditorState,
        newContentState,
        'insert-characters'
      ))

      setWinOpen(false)
      setNewText([])
    }, 100)
  }

  const textLines = selectionText.filter(line => line.trim().length > 0)

  return (
    <FormControl>
      <Button
        sx={{
          maxWidth: '300px',
        }}
        disabled={ disabled }
        onMouseDown={(e) => {
          setSelectionState(getSelection(editorState))
          setSelectionText(getSelectedText(editorState))
          setWinOpen(true)
        }}
      >
        Aria Suggestions
      </Button>
      <Window
        open={ winOpen }
        title="Rewrite your text"
        submitTitle="Accept Suggestion"
        size="lg"
        withCancel
        disabled={ newText.length === 0 }
        onSubmit={ onAcceptNewText }
        onCancel={ closePromptWindow }
      >
        <Grid container spacing={ 2 }>
          <Grid item xs={ 8 }>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 5,
                border: '1px solid #999',
              }}  
            >
              {
                textLines.map((line, i) => {
                  return (
                    <Typography
                      key={ line }
                      sx={{
                        display: 'block',
                        mb: i < textLines.length - 1 ? 2 : 0,
                      }}
                    >
                      { line }
                    </Typography>
                  )
                })
              }
            </Box>
            <Box
              sx={{
                width: '100%',
                mt: 3,
                mb: 3,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}  
            >
              <DownloadIcon
                color='primary'
                sx={{
                  fontSize: '3em'
                }}
              />
            </Box>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 5,
                border: '1px solid #999',
              }}  
            >
              {
                newText.map((line, i) => {
                  return (
                    <Typography
                      key={ line }
                      sx={{
                        display: 'block',
                        mb: i < textLines.length - 1 ? 2 : 0,
                      }}
                    >
                      { line }
                    </Typography>
                  )
                })
              }
            </Box>
          </Grid>
          <Grid item xs={ 4 }>
            {
              prompts.map(prompt => (
                <React.Fragment key={ prompt.title }>
                  <Box
                    sx={{
                      mb: 1,
                    }}
                  >
                    <Button
                      sx={{
                        minWidth: '300px',
                        maxWidth: '300px',
                      }}
                      color="primary"
                      variant="outlined"
                      onClick={(e) => {
                        runPrompt(prompt)
                      }}
                    >
                      { prompt.title }
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      mb: 2,
                    }}
                  >
                    <Typography variant="caption">
                      { prompt.description }
                    </Typography>
                  </Box>
                </React.Fragment>
              ))
            }
          </Grid>
        </Grid>
      </Window>
    </FormControl>
  )
}

export default ToolbarMarketingPrompt